import React, {
  useState,
  useEffect,
  useReducer,
  useContext,
  useRef,
} from "react";

import { toast } from "react-toastify";
import { useParams, useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";

import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import BlockIcon from "@material-ui/icons/Block";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactListItemModal from "../../components/ContactListItemModal";
import ConfirmationModal from "../../components/ConfirmationModal/";

import { i18n } from "../../translate/i18n";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import useContactLists from "../../hooks/useContactLists";
import useColumnAliases from "../../hooks/useColumnAliases";
import { Grid, Dialog, DialogTitle, DialogContent, DialogActions, Typography, CircularProgress, LinearProgress, Box } from "@material-ui/core";

import planilhaExemplo from "../../assets/planilha.xlsx";
import { SocketContext } from "../../context/Socket/SocketContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;
    
    // ✅ CORRECCIÓN: Validar que contacts sea un array antes de usar forEach
    if (!Array.isArray(contacts)) {
      console.warn("LOAD_CONTACTS: payload no es un array, usando array vacío");
      return state;
    }
    
    const newContacts = [];

    contacts.forEach((contact) => {
      const contactIndex = state.findIndex((c) => c.id === contact.id);
      if (contactIndex !== -1) {
        state[contactIndex] = contact;
      } else {
        newContacts.push(contact);
      }
    });

    return [...state, ...newContacts];
  }

  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contact.id);

    if (contactIndex !== -1) {
      state[contactIndex] = contact;
      return [...state];
    } else {
      return [contact, ...state];
    }
  }

  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;

    const contactIndex = state.findIndex((c) => c.id === contactId);
    if (contactIndex !== -1) {
      state.splice(contactIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  // ✅ ESTILOS PRINCIPALES MEJORADOS
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  
  // ✅ ESTILOS DE TABLA MEJORADOS (referencia QuickMessages)
  tableHeader: {
    backgroundColor: '#1976d2', // Azul principal
    color: 'white',
    fontWeight: 'bold',
    fontSize: '0.875rem',
    padding: theme.spacing(1.5),
  },
  
  // ✅ ESTILOS DE BOTONES MEJORADOS
  primaryButton: {
    backgroundColor: '#1976d2',
    color: 'white',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    padding: theme.spacing(1, 2),
    borderRadius: 8,
    '&:hover': {
      backgroundColor: '#1565c0',
    },
  },
  
  // ✅ ESTILOS DE ICONOS MEJORADOS
  actionIcon: {
    color: '#1976d2',
    '&:hover': {
      backgroundColor: 'rgba(25, 118, 210, 0.1)',
    },
  },
  
  deleteIcon: {
    color: '#d32f2f',
    '&:hover': {
      backgroundColor: 'rgba(211, 47, 47, 0.1)',
    },
  },
  
  // ✅ ESTILOS DE BÚSQUEDA MEJORADOS
  searchField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      backgroundColor: '#f5f5f5',
      '&:hover': {
        backgroundColor: '#eeeeee',
      },
      '&.Mui-focused': {
        backgroundColor: 'white',
        boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)',
      },
    },
  },
}));

const ContactListItems = () => {
  const classes = useStyles();

  const { user } = useContext(AuthContext);
  const { contactListId } = useParams();
  const history = useHistory();
  const { getAlias } = useColumnAliases();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(reducer, []);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactListItemModalOpen, setContactListItemModalOpen] =
    useState(false);
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const [contactList, setContactList] = useState({});
  const fileUploadRef = useRef(null);
  
  // ✅ PASO 1: Estados para modal de progreso de importación
  const [importProgressModalOpen, setImportProgressModalOpen] = useState(false);
  const [importProgress, setImportProgress] = useState({
    processed: 0,
    total: 0,
    percentage: 0,
    message: ""
  });

  const { findById: findContactList } = useContactLists();

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    findContactList(contactListId).then((data) => {
      setContactList(data);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactListId]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get(`contact-list-items`, {
            params: { searchParam, pageNumber, contactListId },
          });
          dispatch({ type: "LOAD_CONTACTS", payload: data.contacts });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, contactListId]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);
    
    // ✅ CORRECCIÓN: Flag para verificar si el componente está montado
    let isMounted = true;

    socket.on(`company-${companyId}-ContactListItem`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTS", payload: data.record });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.id });
      }

      if (data.action === "reload") {
        // ✅ CORRECCIÓN: Recargar desde la API en lugar de usar data.records (que puede ser undefined)
        // Esto asegura que se obtengan todos los contactos actualizados
        api.get(`contact-list-items`, {
          params: { searchParam: "", pageNumber: 1, contactListId },
        }).then(({ data: apiData }) => {
          // ✅ CORRECCIÓN: Solo actualizar estado si el componente está montado
          if (isMounted) {
            dispatch({ type: "LOAD_CONTACTS", payload: apiData.contacts || [] });
            setHasMore(apiData.hasMore);
            setPageNumber(1);
          }
        }).catch((err) => {
          if (isMounted) {
            toastError(err);
          }
        });
      }
    });

    socket.on(
      `company-${companyId}-ContactListItem-${contactListId}`,
      (data) => {
        if (data.action === "reload") {
          // ✅ CORRECCIÓN: Recargar desde la API en lugar de usar data.records
          // Esto asegura que se obtengan todos los contactos actualizados
          api.get(`contact-list-items`, {
            params: { searchParam: "", pageNumber: 1, contactListId },
          }).then(({ data: apiData }) => {
            // ✅ CORRECCIÓN: Solo actualizar estado si el componente está montado
            if (isMounted) {
              dispatch({ type: "LOAD_CONTACTS", payload: apiData.contacts || [] });
              setHasMore(apiData.hasMore);
              setPageNumber(1);
            }
          }).catch((err) => {
            if (isMounted) {
              toastError(err);
            }
          });
        }
      }
    );

    // ✅ PASO 3: Listener para eventos de importación de contactos
    socket.on(`company-${companyId}-importContacts`, (data) => {
      // ✅ CORRECCIÓN: Solo procesar eventos si el componente está montado
      if (!isMounted) return;
      
      // ✅ PASO 3: Manejar evento "start"
      if (data.action === "start") {
        setImportProgress({
          processed: 0,
          total: 0,
          percentage: 0,
          message: data.message || "Iniciando importación..."
        });
        setImportProgressModalOpen(true);
      }
      
      // ✅ PASO 3: Manejar evento "progress"
      if (data.action === "progress") {
        const progress = data.progress || {};
        setImportProgress({
          processed: progress.processed || 0,
          total: progress.total || 0,
          percentage: progress.percentage || 0,
          message: data.message || `Procesando: ${progress.processed || 0}/${progress.total || 0} contactos`
        });
      }
      
      // ✅ PASO 3: Manejar evento "completed"
      if (data.action === "completed") {
        const summary = data.summary || {};
        // ✅ CORRECCIÓN: Mensaje amigable para el usuario (no mostrar código técnico)
        const successCount = summary.success || 0;
        const totalCount = summary.total || 0;
        let friendlyMessage = `Importación completada: ${successCount} de ${totalCount} contactos procesados exitosamente`;
        
        // Agregar información adicional si hay advertencias
        const warnings = [];
        if (summary.duplicate > 0) warnings.push(`${summary.duplicate} duplicados`);
        if (summary.error > 0) warnings.push(`${summary.error} errores`);
        if (summary.invalid_number > 0) warnings.push(`${summary.invalid_number} números inválidos`);
        if (summary.empty_number > 0) warnings.push(`${summary.empty_number} números vacíos`);
        if (summary.short_number > 0) warnings.push(`${summary.short_number} números muy cortos`);
        
        if (warnings.length > 0) {
          friendlyMessage += `. Advertencias: ${warnings.join(', ')}. Revisa el reporte descargado para más detalles.`;
        }
        
        setImportProgress({
          processed: totalCount,
          total: totalCount,
          percentage: 100,
          message: friendlyMessage
        });
        
        // ✅ PASO 3: Descargar reporte si está disponible
        if (data.report) {
          // ✅ CORRECCIÓN: Forzar descarga usando fetch + blob para evitar que el navegador muestre el XML
          try {
            // Normalizar backslashes a forward slashes (por si acaso viene de Windows)
            let reportUrl = String(data.report).replace(/\\/g, '/');
            
            // Asegurar que empiece con '/' para construir la URL correctamente
          if (!reportUrl.startsWith('/')) {
            reportUrl = '/' + reportUrl;
          }
          
          // Construir URL completa
          const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin.replace(':3000', ':8080');
          const downloadUrl = `${backendUrl}${reportUrl}`;
          
            // Descargar usando fetch + blob para forzar descarga (evita que el navegador muestre el XML)
            fetch(downloadUrl)
              .then(response => response.blob())
              .then(blob => {
                const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
                link.href = url;
          link.setAttribute('download', 'reporte-importacion.xlsx');
          document.body.appendChild(link);
          link.click();
          link.remove();
                window.URL.revokeObjectURL(url);
              })
              .catch(error => {
                console.error('Error descargando reporte:', error);
                toast.error('Error al descargar el reporte. Por favor, intente nuevamente.');
              });
          } catch (error) {
            console.error('Error procesando descarga:', error);
            toast.error('Error al procesar la descarga del reporte.');
          }
        }
        
        // Mostrar resumen en toast (usar las mismas warnings ya construidas arriba)
        const successMsg = `✅ Importación completada: ${summary.success || 0} procesados`;
        const toastWarnings = [];
        if (summary.duplicate > 0) toastWarnings.push(`${summary.duplicate} duplicados`);
        if (summary.error > 0) toastWarnings.push(`${summary.error} errores`);
        if (summary.invalid_number > 0) toastWarnings.push(`${summary.invalid_number} números inválidos`);
        if (summary.empty_number > 0) toastWarnings.push(`${summary.empty_number} números vacíos`);
        if (summary.short_number > 0) toastWarnings.push(`${summary.short_number} números muy cortos`);
        
        if (toastWarnings.length > 0) {
          toast.warning(`${successMsg}. ⚠️ ${toastWarnings.join(', ')}. Revisa el reporte descargado.`);
        } else {
          toast.success(`${successMsg}. Reporte descargado.`);
        }
        
        // ✅ CORRECCIÓN: Recargar lista de contactos desde la API
        dispatch({ type: "RESET" });
        setPageNumber(1);
        // Recargar contactos
        api.get(`contact-list-items`, {
          params: { searchParam: "", pageNumber: 1, contactListId },
        }).then(({ data }) => {
          // ✅ CORRECCIÓN: Solo actualizar estado si el componente está montado
          if (isMounted) {
            dispatch({ type: "LOAD_CONTACTS", payload: data.contacts || [] });
            setHasMore(data.hasMore);
          }
        }).catch((err) => {
          if (isMounted) {
            toastError(err);
          }
        });
      }
      
      // ✅ PASO 3: Manejar evento "error"
      if (data.action === "error") {
        if (isMounted) {
          setImportProgressModalOpen(false);
          toastError(new Error(data.message || "Error durante la importación"));
        }
      }
    });

    return () => {
      // ✅ CORRECCIÓN: Marcar componente como desmontado y limpiar todos los listeners
      isMounted = false;
      socket.off(`company-${companyId}-ContactListItem`);
      socket.off(`company-${companyId}-ContactListItem-${contactListId}`);
      socket.off(`company-${companyId}-importContacts`);
      // No desconectar el socket completamente, solo remover los listeners específicos
      // socket.disconnect(); // ❌ NO desconectar - otros componentes pueden estar usando el socket
    };
  }, [contactListId, socketManager]);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenContactListItemModal = () => {
    setSelectedContactId(null);
    setContactListItemModalOpen(true);
  };

  const handleCloseContactListItemModal = () => {
    setSelectedContactId(null);
    setContactListItemModalOpen(false);
  };

  const hadleEditContact = (contactId) => {
    setSelectedContactId(contactId);
    setContactListItemModalOpen(true);
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await api.delete(`/contact-list-items/${contactId}`);
      toast.success(i18n.t("contacts.toasts.deleted"));
      setDeletingContact(null);
      setSearchParam("");
      setPageNumber(1);
    } catch (err) {
      // ✅ CORRECCIÓN: Mostrar error en modal en lugar de toast
      const errorMsg = err?.response?.data?.error || err?.response?.data?.message || err?.message || "Error al eliminar el contacto";
      setErrorMessage(errorMsg);
      setErrorModalOpen(true);
      // No cerrar el modal de confirmación si hay error, para que el usuario pueda intentar de nuevo
    }
  };

  // ✅ PASO 1: Modificar handleImportContacts para proceso asíncrono
  const handleImportContacts = async () => {
    try {
      const formData = new FormData();
      formData.append("file", fileUploadRef.current.files[0]);
      
      // ✅ PASO 1: Enviar petición y recibir respuesta inmediata (200 OK)
      const response = await api.request({
        url: `contact-lists/${contactListId}/upload`,
        method: "POST",
        data: formData,
        timeout: 30000, // 30 segundos timeout (suficiente para recibir 200 OK)
      });
      
      // ✅ PASO 1: Si la respuesta es exitosa, abrir modal de progreso
      if (response.status === 200) {
        // Inicializar progreso
        setImportProgress({
          processed: 0,
          total: 0,
          percentage: 0,
          message: "Iniciando importación..."
        });
        // Abrir modal de progreso
        setImportProgressModalOpen(true);
        // Cerrar modal de confirmación
        setConfirmOpen(false);
        }
      
      // ✅ PASO 1: NO esperar respuesta completa - el proceso continúa en background
      // Los eventos Socket.IO actualizarán el progreso en tiempo real
      
    } catch (err) {
      // ✅ PASO 1: Si hay error al iniciar, cerrar modal y mostrar error
      setImportProgressModalOpen(false);
      toastError(err);
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const goToContactLists = () => {
    history.push("/contact-lists");
  };

  const goToCampaigns = () => {
    history.push("/campaigns");
    setErrorModalOpen(false);
  };

  return (
    <MainContainer className={classes.mainContainer}>
      {/* ✅ CORRECCIÓN: Modal de error para mostrar mensajes de validación del backend */}
      <Dialog
        open={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        aria-labelledby="error-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="error-dialog-title" style={{ color: "#d32f2f" }}>
          <span role="img" aria-label="Advertencia">⚠️</span> No se puede eliminar el contacto
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" style={{ whiteSpace: "pre-line" }}>
            {errorMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setErrorModalOpen(false)}
            color="default"
            variant="outlined"
          >
            Cerrar
          </Button>
          <Button
            onClick={goToCampaigns}
            color="primary"
            variant="contained"
          >
            Ir a Campañas
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* ✅ PASO 2: Modal de progreso de importación */}
      <Dialog
        open={importProgressModalOpen}
        onClose={() => {}} // No permitir cerrar mientras está procesando
        aria-labelledby="import-progress-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="import-progress-dialog-title">
          Importando Contactos
        </DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" alignItems="center" p={2}>
            {/* ✅ MEJORA UX: Mostrar spinner solo si no está completo, check si está al 100% */}
            {importProgress.percentage < 100 ? (
              <CircularProgress size={60} thickness={4} style={{ marginBottom: 20 }} />
            ) : (
              <CheckCircleIcon 
                style={{ 
                  fontSize: 60, 
                  color: '#4caf50', 
                  marginBottom: 20 
                }} 
              />
            )}
            
            {/* ✅ PASO 2: Contador X/Y contactos */}
            <Typography variant="h6" style={{ marginBottom: 10, fontWeight: 'bold' }}>
              {importProgress.processed}/{importProgress.total} contactos
            </Typography>
            
            {/* ✅ PASO 2: Barra de progreso lineal */}
            <Box width="100%" style={{ marginBottom: 10 }}>
              <LinearProgress 
                variant="determinate" 
                value={importProgress.percentage} 
                style={{ 
                  height: 8, 
                  borderRadius: 4,
                  backgroundColor: importProgress.percentage === 100 ? '#4caf50' : undefined
                }}
              />
            </Box>
            
            {/* ✅ PASO 2: Porcentaje */}
            <Typography variant="body1" color="textSecondary">
              {importProgress.percentage}% completado
            </Typography>
            
            {/* ✅ PASO 2: Mensaje de estado */}
            {importProgress.message && (
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 10, textAlign: 'center' }}>
                {importProgress.message}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setImportProgressModalOpen(false)} 
            color="primary"
            disabled={importProgress.percentage < 100}
          >
            {importProgress.percentage >= 100 ? "Cerrar" : "Cancelar"}
          </Button>
        </DialogActions>
      </Dialog>
      
      <ContactListItemModal
        open={contactListItemModalOpen}
        onClose={handleCloseContactListItemModal}
        aria-labelledby="form-dialog-title"
        contactId={selectedContactId}
      ></ContactListItemModal>
      <ConfirmationModal
        title={
          deletingContact
            ? `${i18n.t("contactListItems.confirmationModal.deleteTitle")} ${
                deletingContact.name
              }?`
            : `${i18n.t("contactListItems.confirmationModal.importTitle")}`
        }
        open={confirmOpen}
        onClose={(close) => {
          setConfirmOpen(close);
          if (close && deletingContact) {
            setDeletingContact(null);
          }
        }}
        onConfirm={() =>
          deletingContact
            ? handleDeleteContact(deletingContact.id)
            : handleImportContacts()
        }
      >
        {deletingContact ? (
          `${i18n.t("contactListItems.confirmationModal.deleteMessage")}`
        ) : (
          <>
            {i18n.t("contactListItems.confirmationModal.importMessage")}
            <a href={planilhaExemplo} download="planilha.xlsx">
              Haz clic aquí para descargar plantilla de ejemplo.
            </a>
          </>
        )}
      </ConfirmationModal>
      <MainHeader>
        <Grid style={{ width: "99.6%" }} container>
          <Grid xs={12} sm={5} item>
            <Title>{contactList.name}</Title>
          </Grid>
          <Grid xs={12} sm={7} item>
            <Grid spacing={2} container>
              <Grid xs={12} sm={6} item>
                <TextField
                  fullWidth
                  placeholder={i18n.t("contactListItems.searchPlaceholder")}
                  type="search"
                  value={searchParam}
                  onChange={handleSearch}
                  className={classes.searchField}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ color: "#1976d2" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid xs={4} sm={2} item>
                <Button
                  fullWidth
                  variant="contained"
                  className={classes.primaryButton}
                  onClick={goToContactLists}
                >
                  {i18n.t("contactListItems.buttons.lists")}
                </Button>
              </Grid>
              <Grid xs={4} sm={2} item>
                <Button
                  fullWidth
                  variant="contained"
                  className={classes.primaryButton}
                  onClick={() => {
                    fileUploadRef.current.value = null;
                    fileUploadRef.current.click();
                  }}
                >
                  {i18n.t("contactListItems.buttons.import")}
                </Button>
              </Grid>
              <Grid xs={4} sm={2} item>
                <Button
                  fullWidth
                  variant="contained"
                  className={classes.primaryButton}
                  onClick={handleOpenContactListItemModal}
                >
                  {i18n.t("contactListItems.buttons.add")}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MainHeader>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        <>
          <input
            style={{ display: "none" }}
            id="upload"
            name="file"
            type="file"
            accept=".xls,.xlsx"
            onChange={() => {
              setConfirmOpen(true);
            }}
            ref={fileUploadRef}
          />
        </>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center" style={{ width: "0%" }} className={classes.tableHeader}>
                #
              </TableCell>
              <TableCell className={classes.tableHeader}>{i18n.t("contactListItems.table.name")}</TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                {i18n.t("contactListItems.table.number")}
              </TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                {i18n.t("contactListItems.table.email")}
              </TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                {getAlias("Columna 4")}
              </TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                {getAlias("Columna 5")}
              </TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                {getAlias("Columna 6")}
              </TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                {i18n.t("contactListItems.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {contacts.map((contact) => (
                <TableRow key={contact.id}>
                  <TableCell align="center" style={{ width: "0%" }}>
                    <IconButton>
                      {contact.isWhatsappValid ? (
                        <CheckCircleIcon
                          titleAccess="Whatsapp Válido"
                          htmlColor="green"
                        />
                      ) : (
                        <BlockIcon
                          titleAccess="Whatsapp Inválido"
                          htmlColor="grey"
                        />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell>{contact.name}</TableCell>
                  <TableCell align="center">{contact.number}</TableCell>
                  <TableCell align="center">{contact.email}</TableCell>
                  <TableCell align="center">{contact.colum4 != null && contact.colum4 !== '' ? String(contact.colum4) : '-'}</TableCell>
                  <TableCell align="center">{contact.colum5 != null && contact.colum5 !== '' ? String(contact.colum5) : '-'}</TableCell>
                  <TableCell align="center">{contact.colum6 != null && contact.colum6 !== '' ? String(contact.colum6) : '-'}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => hadleEditContact(contact.id)}
                      className={classes.actionIcon}
                      title="Editar Contacto"
                    >
                      <EditIcon />
                    </IconButton>
                    <Can
                      role={user.profile}
                      perform="contacts-page:deleteContact"
                      yes={() => (
                        <IconButton
                          size="small"
                          onClick={() => {
                            setConfirmOpen(true);
                            setDeletingContact(contact);
                          }}
                          className={classes.deleteIcon}
                          title="Eliminar Contacto"
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      )}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={4} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default ContactListItems;
